import React, { useCallback, useEffect, useRef, useState } from 'react';
import { Badge, Button, Dialog, DialogActions, DialogContent, DialogTitle, IconButton, List, ListItem, ListItemIcon, ListItemText, Popover, Typography } from '@material-ui/core';
import { makeStyles } from '@material-ui/core/styles';
import NotificationsIcon from '@material-ui/icons/Notifications';
import AnnouncementIcon from '@material-ui/icons/Announcement';
import API_Service, { axiosDefaultConfig } from '../../Services/API_Service';
import { req_announcement_unreadcount } from '../../Helper/Constants';

const POLL_INTERVAL = 60000;

const useStyles = makeStyles((theme) => ({
	panel: {
		width: 340,
		padding: theme.spacing(1)
	},
	panelTitle: {
		fontSize: 11,
		fontWeight: 700,
		textTransform: 'uppercase',
		letterSpacing: 0.5,
		color: theme.palette.text.secondary,
		padding: theme.spacing(1)
	},
	emptyText: {
		padding: theme.spacing(2),
		fontSize: 13,
		color: theme.palette.text.secondary
	},
	itemIcon: {
		minWidth: 34
	},
	secondaryCritical: {
		color: '#FF5E5B'
	}
}));

export default function AnnouncementBell({ client, language, onOpenAnnouncement }) {
	const classes = useStyles();
	const [unread, setUnread] = useState({ count: 0, items: [] });
	const [anchorEl, setAnchorEl] = useState(null);
	const [popupOpen, setPopupOpen] = useState(false);
	const popupShown = useRef(false);

	const fetchUnread = useCallback(() => {
		if (!client || client === 'easymdemo') return;
		API_Service.get(req_announcement_unreadcount, axiosDefaultConfig(client)).then(({ data }) => {
			if (parseInt(data.result) === 1 && data.extraData) {
				const count = parseInt(data.extraData.count) || 0;
				setUnread({ count, items: data.extraData.items || [] });
				if (count > 0 && !popupShown.current) {
					popupShown.current = true;
					setPopupOpen(true);
				}
			}
		}).catch(() => {
			// silent by design — next poll retries (spec)
		});
	}, [client]);

	useEffect(() => {
		if (!client || client === 'easymdemo') {
			return undefined;
		}
		fetchUnread();
		const timer = setInterval(fetchUnread, POLL_INTERVAL);
		const onRead = () => fetchUnread();
		window.addEventListener('announcement-read', onRead);
		return () => {
			clearInterval(timer);
			window.removeEventListener('announcement-read', onRead);
		};
	}, [client, fetchUnread]);

	if (!client || client === 'easymdemo') {
		return null;
	}

	const openAnnouncementPage = () => {
		setAnchorEl(null);
		setPopupOpen(false);
		onOpenAnnouncement();
	};

	const priorityLabel = (p) => {
		if (p === 'important') return language.announcement_priority_important;
		if (p === 'critical') return language.announcement_priority_critical;
		return language.announcement_priority_info;
	};

	return (
		<>
			<IconButton color="inherit" onClick={(e) => setAnchorEl(e.currentTarget)}>
				<Badge badgeContent={unread.count} color="error">
					<NotificationsIcon />
				</Badge>
			</IconButton>
			<Popover
				open={Boolean(anchorEl)}
				anchorEl={anchorEl}
				onClose={() => setAnchorEl(null)}
				anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
				transformOrigin={{ vertical: 'top', horizontal: 'right' }}>
				<div className={classes.panel}>
					<Typography className={classes.panelTitle}>{language.announcement_notifications}</Typography>
					{unread.items.length === 0 ? (
						<Typography className={classes.emptyText}>{language.announcement_no_unread}</Typography>
					) : (
						<List dense>
							{unread.items.map((item) => (
								<ListItem button key={String(item.id)} onClick={openAnnouncementPage}>
									<ListItemIcon className={classes.itemIcon}>
										<AnnouncementIcon fontSize="small" />
									</ListItemIcon>
									<ListItemText
										primary={item.title}
										secondary={priorityLabel(item.priority)}
										secondaryTypographyProps={item.priority === 'critical' ? { className: classes.secondaryCritical } : undefined}
									/>
								</ListItem>
							))}
						</List>
					)}
					<Button fullWidth size="small" onClick={openAnnouncementPage}>
						{language.announcement_view}
					</Button>
				</div>
			</Popover>
			<Dialog open={popupOpen} onClose={() => setPopupOpen(false)} maxWidth="sm" fullWidth>
				<DialogTitle>{language.announcement_new_popup_title}</DialogTitle>
				<DialogContent>
					<Typography variant="body2">{language.announcement_new_popup_desc}</Typography>
					<List dense>
						{unread.items.map((item) => (
							<ListItem key={String(item.id)}>
								<ListItemIcon className={classes.itemIcon}>
									<AnnouncementIcon fontSize="small" />
								</ListItemIcon>
								<ListItemText primary={item.title} secondary={priorityLabel(item.priority)} secondaryTypographyProps={item.priority === 'critical' ? { className: classes.secondaryCritical } : undefined} />
							</ListItem>
						))}
					</List>
				</DialogContent>
				<DialogActions>
					<Button onClick={() => setPopupOpen(false)}>{language.cancel}</Button>
					<Button color="primary" variant="contained" onClick={openAnnouncementPage}>
						{language.announcement_view}
					</Button>
				</DialogActions>
			</Dialog>
		</>
	);
}
